import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../stores/AuthContext';
import { verifyCheckoutSession } from '../../services/billingService';
import { trackCheckoutCompleted } from '../../services/analytics';

/**
 * Post-checkout callback page.
 * Verifies the checkout session directly with Stripe (doesn't rely on webhooks),
 * refreshes subscription data, and redirects to dashboard.
 */
export default function CheckoutSuccess() {
  const { refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const handleSuccess = async () => {
      try {
        const sessionId = searchParams.get('session_id');

        if (sessionId) {
          // Verify the checkout session directly — this writes the subscription
          // to the DB even if the webhook hasn't arrived yet
          const result = await verifyCheckoutSession(sessionId);

          if (result.plan_id) {
            setPlanName(result.plan_id.charAt(0).toUpperCase() + result.plan_id.slice(1));
            trackCheckoutCompleted({ plan_id: result.plan_id, session_id: sessionId });
          }
        }

        // Refresh user data to pick up new subscription
        await refreshSubscription();
        setStatus('success');

        // Redirect to dashboard after showing success
        setTimeout(() => navigate('/dashboard'), 2500);
      } catch (error) {
        console.error('Error processing checkout success:', error);
        setStatus('error');
        // Still redirect even on error — subscription may activate via webhook later
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    handleSuccess();
  }, [refreshSubscription, navigate, searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4 max-w-md mx-4">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Activating your subscription...</h2>
            <p className="text-sm text-muted-foreground">This will only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Welcome to {planName || 'your new plan'}!
            </h2>
            <p className="text-sm text-muted-foreground">
              Your subscription is active. Redirecting to your dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Payment received!</h2>
            <p className="text-sm text-muted-foreground">
              Your subscription may take a moment to activate. Redirecting...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
